import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { formatPrice } from "@/lib/utils";
import { DailyTrend } from "@/redux/services/dashboard/dashboard.type";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dashboardColors,
  dashboardShadows,
  dashboardSpacing,
} from "@/lib/theme";
import { useRestaurantCurrency } from "@/lib/hooks";

interface DailyTrendsChartProps {
  data: DailyTrend[];
  previousData?: DailyTrend[];
  loading?: boolean;
  /** Chart canvas height in px. Defaults to 350 (dashboard size). */
  height?: number;
}

export const DailyTrendsChart: React.FC<DailyTrendsChartProps> = ({
  data,
  previousData,
  loading,
  height = 350,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<
    "both" | "orders" | "revenue"
  >("both");
  const currency = useRestaurantCurrency();

  const isHourlyData = data.length > 0 && data[0].date.includes(" ");

  if (loading) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  // Build the series together with their colour / stroke / marker styling so the
  // parallel option arrays never drift out of alignment, whatever combination of
  // metrics and comparison is shown. Each metric's current and previous lines are
  // kept adjacent so the legend reads in logical pairs.
  const series: ApexAxisChartSeries = [];
  const colors: string[] = [];
  const strokeWidths: number[] = [];
  const strokeDashArray: number[] = [];
  const markerSizes: number[] = [];

  const showOrders =
    selectedMetric === "both" || selectedMetric === "orders";
  const showRevenue =
    selectedMetric === "both" || selectedMetric === "revenue";

  const pushSeries = (
    name: string,
    seriesData: number[],
    color: string,
    isPrevious: boolean
  ) => {
    series.push({ name, type: "line", data: seriesData });
    colors.push(color);
    strokeWidths.push(isPrevious ? 2 : 3);
    strokeDashArray.push(isPrevious ? 5 : 0);
    markerSizes.push(isPrevious ? 0 : 4);
  };

  if (showOrders) {
    pushSeries(
      "Commandes",
      data.map((item) => item.orders),
      dashboardColors.primary,
      false
    );
    if (previousData) {
      pushSeries(
        "Commandes (préc.)",
        previousData.map((item) => item.orders),
        dashboardColors.primary + "80",
        true
      );
    }
  }

  if (showRevenue) {
    pushSeries(
      "Revenus",
      data.map((item) => item.revenue / 100),
      dashboardColors.success,
      false
    );
    if (previousData) {
      pushSeries(
        "Revenus (préc.)",
        previousData.map((item) => item.revenue / 100),
        dashboardColors.success + "80",
        true
      );
    }
  }

  const options: ApexOptions = {
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
    },
    colors,
    chart: {
      fontFamily: "Inter, sans-serif",
      type: "line",
      height,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
          yaxis: [
            {
              show: false,
            },
            {
              show: false,
            },
          ],
        },
      },
    ],
    stroke: {
      width: strokeWidths,
      curve: "smooth",
      dashArray: strokeDashArray,
    },
    markers: {
      size: markerSizes,
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    grid: {
      borderColor: dashboardColors.border,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: data.map((item) => {
        if (isHourlyData) {
          // For hourly data, show hour in 24h format
          return format(new Date(item.date), "HH'h'", { locale: fr });
        } else {
          // For daily data, show day and month
          return format(new Date(item.date), "dd MMM", { locale: fr });
        }
      }),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: dashboardColors.textSecondary,
          fontSize: "12px",
        },
      },
    },
    yaxis: [
      {
        // Bind both the current and previous orders lines to this axis (1–5
        // scale). Without this, ApexCharts maps series to axes by index, so the
        // 3rd series (previous orders) fell onto the revenue axis and rendered
        // flat at zero (SFRESTO-87 / known prod bug).
        seriesName: ["Commandes", "Commandes (préc.)"],
        title: {
          text: "Commandes",
          style: {
            color: dashboardColors.primary,
            fontSize: "12px",
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: dashboardColors.textSecondary,
            fontSize: "12px",
          },
        },
      },
      {
        opposite: true,
        seriesName: ["Revenus", "Revenus (préc.)"],
        title: {
          text: "Revenus",
          style: {
            color: dashboardColors.success,
            fontSize: "12px",
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: dashboardColors.textSecondary,
            fontSize: "12px",
          },
          formatter: (value: number) => {
            if (value >= 1_000_000) {
              const millions = value / 1_000_000;
              return `${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
            }
            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}k`;
            }
            return value.toString();
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: any) => {
        const date = data[dataPointIndex].date;
        const formattedDate = isHourlyData
          ? format(new Date(date), "dd MMMM yyyy 'à' HH'h'", { locale: fr })
          : format(new Date(date), "dd MMMM yyyy", { locale: fr });

        let tooltipHtml = `
          <div class="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <p class="font-medium text-gray-900 mb-2">${formattedDate}</p>
            <div class="space-y-1">
        `;

        if (selectedMetric === "both" || selectedMetric === "orders") {
          tooltipHtml += `
            <div class="flex items-center justify-between gap-3 text-sm">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded" style="background-color: ${dashboardColors.primary}"></div>
                <span>Commandes:</span>
              </div>
              <span class="font-medium">${data[dataPointIndex].orders}</span>
            </div>
          `;
        }

        if (selectedMetric === "both" || selectedMetric === "revenue") {
          tooltipHtml += `
            <div class="flex items-center justify-between gap-3 text-sm">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded" style="background-color: ${
                  dashboardColors.success
                }"></div>
                <span>Revenus:</span>
              </div>
              <span class="font-medium">${formatPrice(
                data[dataPointIndex].revenue / 100,
                currency
              )}</span>
            </div>
          `;
        }

        if (previousData?.[dataPointIndex]) {
          if (selectedMetric === "both" || selectedMetric === "orders") {
            tooltipHtml += `
              <div class="flex items-center justify-between gap-3 text-sm opacity-70">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded border-2 border-dashed" style="border-color: ${dashboardColors.primary}"></div>
                  <span>Commandes (préc.):</span>
                </div>
                <span>${previousData[dataPointIndex].orders}</span>
              </div>
            `;
          }

          if (selectedMetric === "both" || selectedMetric === "revenue") {
            tooltipHtml += `
              <div class="flex items-center justify-between gap-3 text-sm opacity-70">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded border-2 border-dashed" style="border-color: ${
                    dashboardColors.success
                  }"></div>
                  <span>Revenus (préc.):</span>
                </div>
                <span>${formatPrice(
                  previousData[dataPointIndex].revenue / 100,
                  currency
                )}</span>
              </div>
            `;
          }
        }

        tooltipHtml += `
            </div>
          </div>
        `;

        return tooltipHtml;
      },
    },
  };

  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: dashboardColors.bgCard,
        padding: dashboardSpacing.cardPadding,
        boxShadow: dashboardShadows.card,
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3
          className="text-lg font-semibold"
          style={{ color: dashboardColors.textPrimary }}
        >
          {isHourlyData ? "Tendances horaires" : "Tendances quotidiennes"}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedMetric("both")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "both"
                ? "bg-orange-100 text-orange-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Tout
          </button>
          <button
            onClick={() => setSelectedMetric("orders")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "orders"
                ? "bg-orange-100 text-orange-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Commandes
          </button>
          <button
            onClick={() => setSelectedMetric("revenue")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "revenue"
                ? "bg-green-100 text-green-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Revenus
          </button>
        </div>
      </div>
      <div id="dailyTrendsChart">
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={height}
        />
      </div>
    </div>
  );
};
